/**
 * Source-controlled storefront tenancy configuration.
 *
 * Tenant mode is product behavior, not a deployment secret. Keeping it here
 * means single-shop vs marketplace changes are visible in code review and
 * cannot drift silently between environments.
 */

export type StorefrontMode = 'single-shop' | 'multi-shop';

export interface StorefrontConfig {
  /** `single-shop` uses `primaryShopId`; `multi-shop` loads/merges all configured shops. */
  mode: StorefrontMode;
  /** Fallback shop id for single-shop mode, Hasura scope fallback, and empty tenant lists. */
  primaryShopId: string;
  /** Public umbrella brand for marketplace mode. */
  multiShopBrandName: string;
  /** Header/favicon logo for marketplace mode (path under `public/`). */
  multiShopBrandLogo: string;
  /** Fallback logo when a single shop has no `shop_logo` in Hasura (path under `public/`). */
  singleShopFallbackLogo: string;
  /** Optional single-shop display override when `mst_shop.shop_name` is unavailable. */
  singleShopDisplayNameOverride: string;
  /** Source-controlled shop ids merged with `mst_shop` discovery. */
  catalogShopIdOverrides: readonly string[];
  /** Admin / internal shops excluded from storefront catalog, categories, banners, and PDP. */
  catalogShopIdExclusions: readonly string[];
  /** Footer contact + social defaults when env / Hasura fields are empty. */
  footerDefaults: {
    instagram: string;
    whatsapp: string;
    facebook: string;
    youtube: string;
    email: string;
    phone: string;
    address: string;
  };
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const STOREFRONT_CONFIG: StorefrontConfig = {
  mode: 'multi-shop', //you can use single-shop or multi-shop here
  primaryShopId: '009a211a-5a48-454c-97cf-100080af042c',
  multiShopBrandName: 'Aara Collections',
  multiShopBrandLogo: '/aara_nobg_logo.png',
  singleShopFallbackLogo: '/logo.png',
  singleShopDisplayNameOverride: '',
  catalogShopIdOverrides: [],
  catalogShopIdExclusions: [
    '9f2ae4db-d449-41cd-adeb-426a7572085c',
    '009a211a-5a48-454c-97cf-100080af042c',
  ],
  footerDefaults: {
    instagram: 'https://www.instagram.com/anusha_arboutique',
    whatsapp: 'https://wa.me/918822590325',
    facebook: '',
    youtube: '',
    email: '',
    phone: '+91 8220590325',
    address: '',
  },
};

function assertUuid(value: string, fieldName: string): void {
  if (!UUID_RE.test(value)) {
    throw new Error(
      `[storefront-config] ${fieldName} must be a valid shop UUID. Received: ${value || '<empty>'}`
    );
  }
}

function assertStorefrontConfig(config: StorefrontConfig): void {
  assertUuid(config.primaryShopId, 'primaryShopId');

  const seen = new Set<string>();
  for (const shopId of config.catalogShopIdOverrides) {
    assertUuid(shopId, 'catalogShopIdOverrides[]');
    if (seen.has(shopId)) {
      throw new Error(
        `[storefront-config] Duplicate shop id in catalogShopIdOverrides: ${shopId}`
      );
    }
    seen.add(shopId);
  }

  const excludedSeen = new Set<string>();
  for (const shopId of config.catalogShopIdExclusions) {
    assertUuid(shopId, 'catalogShopIdExclusions[]');
    if (excludedSeen.has(shopId)) {
      throw new Error(
        `[storefront-config] Duplicate shop id in catalogShopIdExclusions: ${shopId}`
      );
    }
    excludedSeen.add(shopId);
    if (seen.has(shopId)) {
      throw new Error(
        `[storefront-config] Shop id cannot be in both catalogShopIdOverrides and catalogShopIdExclusions: ${shopId}`
      );
    }
  }
}

assertStorefrontConfig(STOREFRONT_CONFIG);

export const MULTI_SHOP_CONFIG_ENABLED =
  STOREFRONT_CONFIG.mode === 'multi-shop';

export function getConfiguredPrimaryShopId(): string {
  return STOREFRONT_CONFIG.primaryShopId;
}

export function getConfiguredCatalogShopIds(): string[] {
  return [...STOREFRONT_CONFIG.catalogShopIdOverrides];
}

export function getConfiguredCatalogShopExclusions(): string[] {
  return [...STOREFRONT_CONFIG.catalogShopIdExclusions];
}

export function isStorefrontCatalogShopExcluded(shopId: string): boolean {
  return STOREFRONT_CONFIG.catalogShopIdExclusions.includes(shopId);
}
